/**
 * Web sibling of IdleLockOverlay.
 *
 * The mobile overlay re-authenticates with biometrics and offers Sign Out as
 * the fallback. A browser has no biometrics, so here the user re-enters
 * their account password instead (the same check as the login screen) and
 * the session continues with the vault key still in memory. Accounts with
 * an authenticator app enabled cannot be re-verified inline without the
 * code, so they get the Sign Out path, as on the phone. That path keeps the
 * 30-day device-trust cookie, so signing back in on this browser needs the
 * password only; Account → Log out is what forgets the device.
 *
 * Reads the same lock state as the mobile overlay (services/auth/idleLock).
 * Metro never picks `.web.*` files.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CommonActions } from '@react-navigation/native';

import { AppText as Text } from './AppText';
import { API_URL } from '../config';
import { isLocked, subscribeToLock, unlock } from '../services/auth/idleLock';
import { wipeSessionOnLogout } from '../services/auth/logoutWipe';
import { getAuthToken, setAuthToken } from '../services/auth/tokenStorage';
import { navigationRef } from '../services/navigationService';
import { createLogger } from '../utils/logger';
import { ms, s } from '../utils/responsive';

const logger = createLogger('IdleLockOverlay.web');

type Status = 'idle' | 'checking' | 'failed' | 'signout-only';

const IdleLockOverlay: React.FC = () => {
  const [locked, setLocked] = useState<boolean>(isLocked());
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const off = subscribeToLock((isNowLocked) => {
      setLocked(isNowLocked);
      if (isNowLocked) {
        setPassword('');
        setStatus('idle');
        setMessage(null);
      }
    });
    return off;
  }, []);

  // Which account is locked, so the password check targets the right user.
  useEffect(() => {
    if (!locked) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = await AsyncStorage.getItem('@user_email');
        let resolved = stored;
        if (!resolved) {
          const raw = await AsyncStorage.getItem('user');
          resolved = raw ? (JSON.parse(raw) as { email?: string }).email ?? null : null;
        }
        if (!resolved) {
          // Only the login screens write the keys above. A session that
          // began on the email-verification landing (a fresh sign-up) has
          // just the token, so ask the API who this is and remember it.
          const token = await getAuthToken();
          if (token) {
            const res = await fetch(`${API_URL}/users/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = res.ok ? ((await res.json()) as { user?: { email?: string } }) : null;
            resolved = data?.user?.email ?? null;
            if (resolved) await AsyncStorage.setItem('@user_email', resolved);
          }
        }
        if (cancelled) return;
        if (!resolved) {
          setStatus('signout-only');
          setMessage('Sign in again to continue.');
          return;
        }
        setEmail(resolved);
        // Accounts with an authenticator app need the code; not offered inline.
        // With the session, so the server gives the real answer (it answers
        // strangers as if there were no two-step, 2026-09-24).
        const session = await getAuthToken();
        const res = await fetch(`${API_URL}/auth/check-totp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session ? { Authorization: `Bearer ${session}` } : {}),
          },
          body: JSON.stringify({ email: resolved.toLowerCase().trim() }),
        });
        const data = res.ok ? await res.json() : null;
        if (!cancelled && data?.isTotpEnabled) {
          setStatus('signout-only');
          setMessage('Your account uses an authenticator app. Sign in again to continue.');
        }
      } catch (err) {
        logger.warn('Idle-lock: account lookup failed', err);
        if (!cancelled) {
          setStatus('signout-only');
          setMessage('Sign in again to continue.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locked]);

  const handleSignOut = useCallback(async () => {
    logger.info('Idle-lock: user chose Sign Out');
    try {
      // Locking is not logging out: the device stays trusted for its 30 days.
      await wipeSessionOnLogout({ keepDeviceTrust: true });
    } catch {
      logger.error('wipeSessionOnLogout failed in lock overlay');
    }
    unlock();
    if (navigationRef.isReady()) {
      navigationRef.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'Auth', state: { routes: [{ name: 'Login' }] } }],
        }),
      );
    }
  }, []);

  const handleUnlock = useCallback(async () => {
    if (!email || !password) return;
    setStatus('checking');
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
      });
      if (!res.ok) {
        setStatus('failed');
        setMessage(res.status === 401 ? "That password didn't match." : 'Could not verify right now. Try again.');
        return;
      }
      const data = (await res.json()) as {
        access_token?: string;
        requiresSecondFactor?: boolean;
        userId?: string;
        secondFactorTicket?: string;
      };
      let accessToken = data.access_token;
      if (!accessToken && data.requiresSecondFactor) {
        // Two-factor accounts get a ticket for the password, not a session
        // (2026-09-23). A browser remembered for 30 days (the httpOnly
        // cookie) turns it into a session; otherwise the code is needed.
        const trust = await fetch(`${API_URL}/auth/verify-device-trust`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: data.userId,
            deviceTrustToken: 'cookie',
            secondFactorTicket: data.secondFactorTicket,
          }),
        });
        const trusted = trust.ok ? await trust.json() : null;
        if (trusted?.success && trusted?.isTrusted && trusted.access_token) {
          accessToken = trusted.access_token;
        } else {
          setPassword('');
          setStatus('signout-only');
          setMessage('This browser is not remembered for your second step. Sign in again to enter your code.');
          return;
        }
      }
      if (!accessToken) {
        setStatus('failed');
        setMessage('Could not verify right now. Try again.');
        return;
      }
      await setAuthToken(accessToken);
      logger.info('Idle-lock unlocked with password');
      setPassword('');
      unlock();
    } catch (err) {
      logger.error('Idle-lock password check failed', err);
      setStatus('failed');
      setMessage('Could not verify right now. Try again.');
    }
  }, [email, password]);

  if (!locked) return null;

  const canSubmit = status !== 'checking' && status !== 'signout-only' && password.length > 0;

  return (
    <View style={styles.backdrop} accessibilityRole="alert" accessibilityLabel="Locked. Enter your password to continue.">
      <View style={styles.card}>
        <Image source={require('../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Locked</Text>
        <Text style={styles.subtitle}>
          {status === 'signout-only'
            ? message
            : 'You were away for a while. Enter your password to continue.'}
        </Text>

        {status !== 'signout-only' && (
          <>
            <TextInput
              style={styles.input}
              value={password}
              onChangeText={setPassword}
              placeholder="Password"
              placeholderTextColor="#A0AEC0"
              secureTextEntry
              autoFocus
              onSubmitEditing={handleUnlock}
              editable={status !== 'checking'}
              accessibilityLabel="Password"
              testID="idle-lock-password"
            />
            {message ? <Text style={styles.error}>{message}</Text> : null}
            <TouchableOpacity
              style={[styles.button, !canSubmit && styles.buttonDisabled]}
              onPress={handleUnlock}
              disabled={!canSubmit}
              accessibilityRole="button"
              accessibilityLabel="Unlock"
              accessibilityState={{ disabled: !canSubmit }}
            >
              {status === 'checking' ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>Unlock</Text>
              )}
            </TouchableOpacity>
          </>
        )}

        <TouchableOpacity
          onPress={handleSignOut}
          style={styles.signOut}
          accessibilityRole="button"
          accessibilityLabel="Sign out"
        >
          <Text style={styles.signOutText}>{status === 'signout-only' ? 'Sign in again' : 'Sign out instead'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    // `fixed` is valid in react-native-web; the RN types only list the
    // native values, hence the cast.
    position: 'fixed' as unknown as 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(28, 44, 79, 0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(24),
    zIndex: 1000,
  },
  card: {
    width: '100%',
    maxWidth: s(360),
    backgroundColor: '#F3F6F8',
    borderRadius: ms(20),
    padding: ms(24),
    alignItems: 'center',
  },
  logo: {
    width: s(80),
    height: s(80),
    marginBottom: ms(8),
  },
  title: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(6),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    color: '#2C4364',
    textAlign: 'center',
    marginBottom: ms(18),
    lineHeight: ms(21, 0.3),
  },
  input: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    borderWidth: 1,
    borderColor: '#E5E9F0',
    paddingVertical: ms(14),
    paddingHorizontal: ms(18),
    fontSize: ms(16, 0.3),
    color: '#2C4364',
    marginBottom: ms(12),
  },
  error: {
    fontSize: ms(13, 0.3),
    color: '#E85C5C',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  button: {
    width: '100%',
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(14),
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  signOut: {
    marginTop: ms(16),
    minHeight: 44,
    justifyContent: 'center',
  },
  signOutText: {
    color: '#1C2C4F',
    fontSize: ms(15, 0.3),
    textDecorationLine: 'underline',
  },
});

export { IdleLockOverlay };
export default IdleLockOverlay;
